import React, { useState, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiClock, FiUser, FiRotateCcw, FiEye, FiGitBranch, FiTag } = FiIcons

const VersionHistory = ({ projectId, onRestoreVersion, onClose }) => {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedVersion, setSelectedVersion] = useState(null)

  useEffect(() => {
    loadVersionHistory()
  }, [projectId])

  const loadVersionHistory = () => {
    // Simulate loading version history
    setTimeout(() => {
      const mockVersions = [
        {
          id: 'v1.8',
          timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
          author: 'Raymond Gey',
          description: 'Updated character descriptions and added visual elements',
          changes: ['Modified slide 3', 'Added new image to slide 2', 'Updated typography'],
          isCurrent: true,
          size: '2.4 MB',
          slideCount: 7
        },
        {
          id: 'v1.7',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          author: 'Sarah Johnson',
          description: 'Collaboration feedback incorporated',
          changes: ['Fixed typos on slide 1', 'Improved color scheme', 'Added bullet points'],
          isCurrent: false,
          size: '2.3 MB',
          slideCount: 7
        },
        {
          id: 'v1.6',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
          author: 'Raymond Gey',
          description: 'Major content restructure',
          changes: ['Reorganized slide order', 'Added synopsis slide', 'Updated background images'],
          isCurrent: false,
          size: '2.1 MB',
          slideCount: 6,
          isTagged: true,
          tag: 'Review Draft'
        },
        {
          id: 'v1.5',
          timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
          author: 'Mike Chen',
          description: 'Initial AI-generated content',
          changes: ['Created from AI prompt', 'Generated all slides', 'Set up basic structure'],
          isCurrent: false,
          size: '1.8 MB',
          slideCount: 5,
          isTagged: true,
          tag: 'First Draft'
        }
      ]
      setVersions(mockVersions)
      setLoading(false)
    }, 1000)
  }

  const handleRestoreVersion = (version) => {
    if (confirm(`Are you sure you want to restore to ${version.id}? This will create a new version and cannot be undone.`)) {
      onRestoreVersion(version)
      onClose()
    }
  }

  const handlePreviewVersion = (version) => {
    setSelectedVersion(version)
    // In a real app, this would load and display the version content
    alert(`Preview of ${version.id} would be shown here`)
  }

  const formatTimeAgo = (timestamp) => {
    const now = new Date()
    const diffMs = now - timestamp
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  }

  if (loading) {
    return (
      <div className="bg-white border-l border-gray-200 w-80 h-full flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Loading versions...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border-l border-gray-200 w-80 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <SafeIcon icon={FiClock} className="w-5 h-5" />
            <span>Version History</span>
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-gray-600">
          {versions.length} version{versions.length !== 1 ? 's' : ''} saved
        </p>
      </div>

      {/* Versions List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {versions.map((version) => (
            <div
              key={version.id}
              className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                version.isCurrent 
                  ? 'border-primary-500 bg-primary-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Version Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiGitBranch} className={`w-4 h-4 ${version.isCurrent ? 'text-primary-600' : 'text-gray-600'}`} />
                  <span className={`font-semibold ${version.isCurrent ? 'text-primary-900' : 'text-gray-900'}`}>
                    {version.id}
                  </span>
                  {version.isCurrent && (
                    <span className="bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                      Current
                    </span>
                  )}
                  {version.isTagged && (
                    <div className="flex items-center space-x-1 text-xs text-blue-600">
                      <SafeIcon icon={FiTag} className="w-3 h-3" />
                      <span>{version.tag}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Author and Time */}
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">{version.author}</span>
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-500">{formatTimeAgo(version.timestamp)}</span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-700 mb-3">{version.description}</p>

              {/* Changes */}
              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-1">Changes:</p>
                <ul className="space-y-1">
                  {version.changes.slice(0, 2).map((change, index) => (
                    <li key={index} className="text-xs text-gray-600 flex items-start space-x-1">
                      <span className="text-gray-400">•</span>
                      <span>{change}</span>
                    </li>
                  ))}
                  {version.changes.length > 2 && (
                    <li className="text-xs text-gray-500">
                      +{version.changes.length - 2} more change{version.changes.length - 2 !== 1 ? 's' : ''}
                    </li>
                  )}
                </ul>
              </div>

              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>{version.slideCount} slides</span>
                <span>{version.size}</span>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePreviewVersion(version)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                  <span>Preview</span>
                </button>
                {!version.isCurrent && (
                  <button
                    onClick={() => handleRestoreVersion(version)}
                    className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
                    <span>Restore</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          Versions are automatically saved every 5 minutes
        </p>
      </div>
    </div>
  )
}

export default VersionHistory